import { AfterViewInit, Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Subscription } from 'rxjs';
import { Icons } from '../../../models/icons.model';
import { SiteModel } from '../../../models/site.model';
import { UserModel } from '../../../models/user.model';
import { AdminService } from '../../../services/admin.service';
import { SiteService } from '../../../services/site.service';
import { ConfirmDialogComponent } from '../../../shared/components/confirm-dialog/confirm-dialog.component';
import { CameraEditorComponent } from '../../components/camera-editor/camera-editor.component';
import { CsvService } from 'src/app/services/csv.service';

@Component({
  selector: 'app-admin-site-detail',
  standalone: false,
  templateUrl: './admin-site-detail.component.html',
  styleUrls: ['./admin-site-detail.component.scss'],
})
export class AdminSiteDetailComponent implements OnInit, AfterViewInit {
  adminServiceLoadEvent: Subscription;
  siteModel = new SiteModel();
  isNew: boolean;
  alertMsg: string;
  isReady: boolean = true;
  icons = new Icons();
  allUsers: UserModel[] = [];

  get selectedUsers(): UserModel[] {
    return this.allUsers.filter((u) => u.IsSelected);
  }

  constructor(
    public adminService: AdminService,
    private siteService: SiteService,
    private csvService: CsvService,
    private route: ActivatedRoute,
    private router: Router,
    private modal: NgbModal
  ) { }

  ngAfterViewInit() {
    document.scrollingElement.scrollTo(0, 0);
  }

  ngOnInit(): void {
    this.alertMsg = 'Loading Site...';
    this.isReady = false;
    this.route.paramMap.subscribe((params) => {
      if (params.has('id')) {
        if (params.get('id') == 'add') {
          // add new site
          if (!this.adminService.isReady) {
            this.adminServiceLoadEvent = this.adminService.adminServiceLoadedEvent.subscribe(
              () => {
                this.adminServiceLoadEvent.unsubscribe();
                this.initNew();
              }
            );
          } else this.initNew();
        } else {
          // edit site
          const siteId = +params.get('id');
          if (!this.adminService.isReady) {
            this.adminServiceLoadEvent = this.adminService.adminServiceLoadedEvent.subscribe(
              () => {
                this.adminServiceLoadEvent.unsubscribe();
                this.initEdit(siteId);
              }
            );
          } else this.initEdit(siteId);
        }
      } else {
        this.router.navigate(['/admin', 'sites']);
      }
    });
  }

  initEdit(siteId: number) {
    this.isNew = false;
    this.siteModel = { ...this.adminService.sites.find((s) => s.Id == siteId) };
    this.allUsers = JSON.parse(JSON.stringify(this.adminService.users));

    if (this.siteModel) {
      this.allUsers.forEach((user) => {
        const map = this.siteModel.Users.find((m) => m.Id == user.Id);
        user.IsSelected = map ? true : false;
      });
    } else {
      // not found?
    }
    this.isReady = true;
  }

  initNew() {
    this.siteModel = new SiteModel();
    this.allUsers = JSON.parse(JSON.stringify(this.adminService.users));

    this.isNew = true;
    this.isReady = true;
  }

  exportCameras() {
    this.csvService.createSiteCameraInventoryCsv(this.siteModel, `${this.siteModel.Name}_cameras.csv`);
  }

  saveSite() {
    this.alertMsg = 'Saving Site...';
    this.isReady = false;
    this.siteModel.Users = this.allUsers.filter((u) => u.IsSelected);
    let operation: Promise<SiteModel>;

    if (this.isNew) {
      operation = this.siteService.createNewSite(this.siteModel);
    } else {
      operation = this.siteService.updateSite(this.siteModel);
    }
    operation.then(() => {
      this.adminService.reloadService();
      this.router.navigate(['/admin', 'sites']);
    });
  }

  deleteSite() {
    const confirm = this.modal.open(ConfirmDialogComponent, {
      keyboard: true,
      centered: true,
    });

    confirm.componentInstance.dialogTitle = 'Delete Site';
    confirm.componentInstance.dialogMessage =
      'Are you sure you want to delete this Site? All cameras and alerts will be deleted as well.';

    confirm.result
      .then(() => {
        this.alertMsg = 'Deleting site...';
        this.isReady = false;
        this.siteService.deleteSite(this.siteModel.Id).then(() => {
          this.adminService.reloadService();
          this.router.navigate(['/admin', 'sites']);
        });
      })
      .catch(() => {
        console.log('canceled');
      });
  }

  editCamera(index: number) {
    const dlg = this.modal.open(CameraEditorComponent, {
      size: 'lg',
      keyboard: true,
    });

    dlg.componentInstance.cameraModel = { ...this.siteModel.Cameras[index] };

    dlg.result
      .then((rtnval) => {
        // camera saved
        this.siteModel.Cameras[index] = rtnval;
      })
      .catch((err) => {
        if (err == 'deleted') {
          // remove the deleted camera
          const id = this.siteModel.Cameras[index].Id;
          this.siteModel.Cameras = this.siteModel.Cameras.filter(
            (c) => c.Id != id
          );
        }
        // cancelled
      });
  }

  selectAllUsers() {
    this.allUsers.forEach((u) => (u.IsSelected = true));
  }

  selectNoUsers() {
    this.allUsers.forEach((u) => (u.IsSelected = false));
  }
}
